<template>
  <section class="section is-paddingless">
    <iframe v-if="isWidget"
            ref="iframe"/>
    <iframe v-else
            ref="iframe"
            :src="content.micrositeEntryFile" />
  </section>
</template>

<script>
import { mapState, useStore } from 'vuex'
import { ref, computed, onMounted, watch } from 'vue'
import { useMicrositeAddToCart } from './useMicrositeAddToCart'

export default {
  name: 'MicrositeViewer',
  setup() {
    const iframe = ref()

    const store = useStore()

    const isWidget = computed(() => store.getters['widgets/isWidget'])
    const micrositeUrl = computed(() => store.state.content?.micrositeEntryFile)

    useMicrositeAddToCart(iframe, micrositeUrl, isWidget)

    function postAuthToIframe() {
      const target = iframe.value?.contentWindow
      if (!target) return

      const { accessToken, hostedDomain } = store.state.auth
      target.postMessage(
        {
          type: 'WIDGET_AUTH_UPDATE',
          accessToken,
          hostedDomain
        },
        '*'
      )
    }

    async function loadMicrositeIntoIframe() {
      if (!iframe.value || !micrositeUrl.value) return

      const { accessToken, hostedDomain } = store.state.auth

      const res = await fetch(micrositeUrl.value, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Hosted-Domain': hostedDomain
        }
      })

      const html = await res.text()
      const entryBaseUrl = new URL(micrositeUrl.value, window.location.href)
      const entryDirUrl = new URL('./', entryBaseUrl)

      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')

      async function fetchWithAuth(url) {
        const r = await fetch(url, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Hosted-Domain': hostedDomain
          }
        })
        return await r.text()
      }

      function isSameOriginOrRelative(url) {
        try {
          const resolved = new URL(url, entryDirUrl)
          return resolved.origin === window.location.origin
        } catch (e) {
          return false
        }
      }

      async function inlineExternalStylesheets() {
        const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"][href]'))
        for (const link of links) {
          const href = link.getAttribute('href')
          if (!href) continue

          if (!isSameOriginOrRelative(href)) continue

          const resolved = new URL(href, entryDirUrl).href
          try {
            const css = await fetchWithAuth(resolved)
            const styleEl = doc.createElement('style')
            styleEl.type = 'text/css'
            styleEl.appendChild(doc.createTextNode(css))
            link.replaceWith(styleEl)
          } catch (e) {
            // If we can't inline, leave the original link in place.
          }
        }
      }

      async function inlineExternalScripts() {
        const scripts = Array.from(doc.querySelectorAll('script[src]'))
        for (const script of scripts) {
          const src = script.getAttribute('src')
          if (!src) continue

          if (!isSameOriginOrRelative(src)) continue

          const resolved = new URL(src, entryDirUrl).href
          try {
            const js = await fetchWithAuth(resolved)
            const inline = doc.createElement('script')
            const type = script.getAttribute('type')
            if (type) inline.setAttribute('type', type)
            if (script.noModule) inline.noModule = true
            if (script.defer) inline.defer = true
            if (script.async) inline.async = true
            inline.appendChild(doc.createTextNode(js))
            script.replaceWith(inline)
          } catch (e) {
            // If we can't inline, leave the original script tag in place.
          }
        }
      }

      const head = doc.head || doc.getElementsByTagName('head')[0]
      if (head && !head.querySelector('base')) {
        const baseEl = doc.createElement('base')
        baseEl.setAttribute('href', entryDirUrl.href)
        head.prepend(baseEl)
      }

      if (head) {
        const interceptorScript = doc.createElement('script')
        interceptorScript.type = 'text/javascript'
        interceptorScript.text = `(function () {
  var accessToken = ${JSON.stringify(accessToken || '')};
  var hostedDomain = ${JSON.stringify(hostedDomain || '')};

  function withAuthHeaders(headers) {
    var h;
    try {
      h = new Headers(headers || {});
    } catch (e) {
      h = new Headers();
    }
    if (accessToken) h.set('Authorization', 'Bearer ' + accessToken);
    if (hostedDomain) h.set('Hosted-Domain', hostedDomain);
    return h;
  }

  var originalFetch = window.fetch ? window.fetch.bind(window) : null;
  if (originalFetch) {
    window.fetch = function (resource, config) {
      try {
        if (resource instanceof Request) {
          var reqHeaders = withAuthHeaders(resource.headers);
          var req = new Request(resource, { headers: reqHeaders });
          return originalFetch(req, config);
        }

        config = config || {};
        config.headers = withAuthHeaders(config.headers);
        return originalFetch(resource, config);
      } catch (e) {
        return originalFetch(resource, config);
      }
    };
  }

  var oldXHROpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype && window.XMLHttpRequest.prototype.open;
  if (oldXHROpen) {
    window.XMLHttpRequest.prototype.open = function () {
      var extended = oldXHROpen.apply(this, arguments);
      try {
        if (accessToken) this.setRequestHeader('Authorization', 'Bearer ' + accessToken);
        if (hostedDomain) this.setRequestHeader('Hosted-Domain', hostedDomain);
      } catch (e) {}
      return extended;
    };
  }

  window.addEventListener('message', function (e) {
    if (!e || !e.data || e.data.type !== 'WIDGET_AUTH_UPDATE') return;
    accessToken = e.data.accessToken || '';
    hostedDomain = e.data.hostedDomain || '';
  });
})();`
        head.prepend(interceptorScript)
      }

      // Inline authenticated resources before setting srcdoc. When the microsite
      // is rendered via srcdoc/blob, the browser cannot attach Authorization
      // headers to <script src> / <link href> requests.
      await inlineExternalStylesheets()
      await inlineExternalScripts()

      iframe.value.srcdoc = '<!doctype html>\n' + doc.documentElement.outerHTML
      postAuthToIframe()
    }

    onMounted(async () => {
      if (!isWidget.value) return
      await loadMicrositeIntoIframe()
    })

    watch(
      () => store.state.auth.accessToken,
      () => {
        if (!isWidget.value) return
        postAuthToIframe()
      }
    )

    watch(
      () => micrositeUrl.value,
      async () => {
        if (!isWidget.value) return
        await loadMicrositeIntoIframe()
      }
    )

    return { isWidget, iframe }
  },
  computed: {
    ...mapState({
      content: (state) => state.content
    })
  }
}
</script>

<style scoped lang="scss">
.section {
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  position: relative;
}
.image-centered {
  margin: auto;
}
iframe {
  position: absolute;
  width: 100%;
  height: 100%;
}
</style>
